/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  DataCarrierTable,
  FormDatePicker,
  PrimaryButton,
  RedButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import Papa from "papaparse";
import { useDataCarriers } from "../../store/useDataCarriers";
import * as XLSX from "xlsx";

function DataCarriersScreen() {
  /** Refs */
  const fileInputRef = useRef(null);
  /** Hooks */
  const { dataCarriers, getDataCarriers, hasNextPage, addManyDataCarriers } =
    useDataCarriers();
  /** State */
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [uploadedDataCarrier, setUploadedDataCarrier] = useState<
    { grade: string; count: number }[]
  >([]);

  /** Callbacks */
  const handleFileUpload = (event: any) => {
    const file = event.target.files[0];

    if (file) {
      // Check file extension
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (fileExtension === "csv") {
        handleCSV(file);
      } else if (fileExtension === "xlsx" || fileExtension === "xls") {
        handleExcel(file);
      } else {
        console.error("Unsupported file format");
      }
    }
  };

  const handleCSV = (file: File) => {
    return Papa.parse(file, {
      complete: (result: any) => {
        processData(result.data);
      },
    });
  };

  const handleExcel = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Process each sheet
        const allData: any[] = [];
        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const csvData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          allData.push(...csvData);
        });

        processData(allData);
      } catch (error) {
        console.error("Error processing Excel file:", error);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const processData = (data: any[]) => {
    // Find the row index containing "Grade" column
    let headerRowIndex = -1;
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      if (Array.isArray(row)) {
        // For array-like rows
        if (
          row.some(
            (cell) =>
              typeof cell === "string" && cell.toLowerCase().includes("grade")
          )
        ) {
          headerRowIndex = i;
          break;
        }
      } else {
        // For object-like rows
        if (
          Object.keys(row).some((key) => key.toLowerCase().includes("grade"))
        ) {
          headerRowIndex = i;
          break;
        }
      }
    }

    // If header row found, process data from that point
    if (headerRowIndex !== -1) {
      const relevantData = data.slice(headerRowIndex + 1);

      let grades: any[] = [];
      relevantData.forEach((item: any) => {
        // Handle both array and object formats
        const gradeValue = Array.isArray(item)
          ? item[
              data[headerRowIndex].findIndex(
                (header: string) =>
                  typeof header === "string" &&
                  header.toLowerCase().includes("grade")
              )
            ]
          : item?.["Grade "] ||
            item?.["Grade"] ||
            item?.["grade"] ||
            item?.["grade "];

        if (gradeValue) {
          grades.push({ grade: gradeValue });
        }
      });

      const gradeCount: any = {};

      grades.forEach((item) => {
        if (gradeCount[item.grade]) {
          gradeCount[item.grade]++;
        } else {
          gradeCount[item.grade] = 1;
        }
      });

      const groupedGrades = Object.keys(gradeCount).map((grade) => ({
        grade: grade,
        count: gradeCount[grade],
      }));
      setUploadedDataCarrier(groupedGrades);
      setModal(true);
    } else {
      console.error("No Grade column found in the file");
    }
  };

  const openFileUpload = () => {
    if (fileInputRef?.current) {
      //@ts-ignore
      fileInputRef?.current?.click();
    }
  };

  /** Effects */
  useEffect(() => {
    getDataCarriers(page, 20);
  }, []);

  return (
    <div>
      <div className="flex w-full justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          Data Carriers
        </h1>
        <div className="flex flex-col sm:flex-row items-end sm:items-center">
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            accept=".csv,.xlsx,.xls"
            onChange={handleFileUpload}
          />
          <PrimaryButton
            onClick={openFileUpload}
            label="Upload Data Carriers file"
            className="h-10 sm:ml-2 mb-2 sm:mb-0"
          />
        </div>
      </div>
      <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
        {!dataCarriers?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            There is no Data Carrier created yet.
          </p>
        ) : (
          <DataCarrierTable />
        )}
      </div>
      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            onClick={() => {
              setPage(page + 1);
              getDataCarriers(page + 1, 20);
            }}
            label="Load more"
            className="h-10"
          />
        </div>
      ) : null}
      {/* Modal */}
      {modal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Data Carrier Preview
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  {uploadedDataCarrier.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border-b border-solid border-blueGray-200 py-2"
                    >
                      <p>{item.grade}</p>
                      <p>{item.count}</p>
                    </div>
                  ))}
                  <FormDatePicker
                    label="Select date"
                    placeholder="Select date"
                    value={selectedDate ?? new Date()}
                    onChange={setSelectedDate}
                    labelClassName="mt-4"
                  />
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={() => {
                      setModal(false);
                      setUploadedDataCarrier([]);
                      setSelectedDate(new Date());
                    }}
                  />
                  <PrimaryButton
                    disabled={false}
                    label="Upload"
                    onClick={() => {
                      let updatedDataCarrier = uploadedDataCarrier.map(
                        (item) => ({
                          ...item,
                          date: selectedDate ?? new Date(),
                        })
                      );
                      addManyDataCarriers(updatedDataCarrier);
                      setUploadedDataCarrier([]);
                      setSelectedDate(new Date());
                      setModal(false);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(DataCarriersScreen);
